var ViedocRadio_1;
import { __decorate } from "tslib";
import { css, html, LitElement } from 'lit';
import { customElement, property, query } from 'lit/decorators.js';
import { classMap } from 'lit/directives/class-map.js';
let ViedocRadio = ViedocRadio_1 = class ViedocRadio extends LitElement {
    constructor() {
        super();
        this.value = '';
        this.checked = false;
        this.disabled = false;
        this.name = '';
        this._internals = this.attachInternals();
        this._internals.role = 'radio';
    }
    firstUpdated() {
        this.inputElement.tabIndex = this.checked ? 0 : -1;
        // If none of the radio-buttons are initally chosen, make the first radio-button tabbable
        const radios = this.getAllRadios();
        // radios is empty if radios-buttons don´t have names
        if (radios.length > 0 && radios.every(radio => radio.checked === false)) {
            radios[0].inputElement.tabIndex = 0;
        }
    }
    // return all radiobuttons in the same group (same name and part of the same form or no form)
    getAllRadios() {
        const root = this._internals.form || document;
        return Array.from(root.querySelectorAll(`viedoc-radio[name="${this.name}"]`)).filter(node => !!this._internals.form || node.getForm() === null);
    }
    willUpdate(changedProperties) {
        if (changedProperties.has('checked')) {
            this._internals.ariaChecked = this.checked.toString();
            this._internals.setFormValue(this.checked ? this.value : null);
        }
    }
    getForm() {
        return this._internals.form;
    }
    static unCheckAllRadios(radioNames) {
        radioNames.forEach(radio => {
            // eslint-disable-next-line no-param-reassign
            radio.checked = false;
            // eslint-disable-next-line no-param-reassign
            radio.inputElement.tabIndex = -1;
        });
    }
    _handleClick() {
        if (this.checked === this.inputElement.checked)
            return;
        const radios = this.getAllRadios();
        ViedocRadio_1.unCheckAllRadios(radios);
        this.checked = this.inputElement.checked;
        this.inputElement.tabIndex = 0;
        this.dispatchEvent(new Event('change', {
            bubbles: true,
            composed: true,
        }));
    }
    handleKeyDown(event) {
        if (!['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight', ' '].includes(event.key)) {
            return;
        }
        event.preventDefault();
        const radioNames = this.getAllRadios();
        let incr = 0; // eventkey is space
        if (['ArrowUp', 'ArrowLeft'].includes(event.key)) {
            incr = -1;
        }
        else if (['ArrowDown', 'ArrowRight'].includes(event.key)) {
            incr = 1;
        }
        let nextIndex = radioNames.indexOf(this) + incr;
        if (nextIndex < 0) {
            nextIndex = radioNames.length - 1;
        }
        if (nextIndex > radioNames.length - 1) {
            nextIndex = 0;
        }
        ViedocRadio_1.unCheckAllRadios(radioNames);
        const chosenRadio = radioNames[nextIndex];
        chosenRadio.checked = true;
        chosenRadio.inputElement.tabIndex = 0;
        chosenRadio.inputElement.focus();
        chosenRadio.dispatchEvent(new Event('change', {
            bubbles: true,
            composed: true,
        }));
    }
    _renderSvg() {
        if (this.checked) {
            return html `
        <svg viewBox="0 0 24 24">
          <rect width="24" height="24" rx="50" fill="var(--primary-color)" />
          <path
            d="M10.219 14.561 7.355 11.803 6.4 12.722 10.219 16.4 18.4 8.519 17.446 7.6 10.219 14.561Z"
            fill="var(--primary-contrast-color)"
          />
        </svg>
      `;
        }
        return html `
      <svg viewBox="0 0 24 24">
        <rect
          x="1"
          y="1"
          width="22"
          height="22"
          rx="50"
          fill="none"
          stroke="var(--color-gray-30)"
        />
      </svg>
    `;
    }
    render() {
        const classNames = classMap({
            radio: true,
            'radio--disabled': this.disabled,
        });
        return html `
      <label class=${classNames}>
        <input
          type="radio"
          .value=${this.value}
          ?checked=${this.checked}
          ?disabled=${this.disabled}
          name=${this.name}
          @click=${this._handleClick}
          @keydown=${this.handleKeyDown}
        />
        ${this._renderSvg()}
        <span class="text"><slot></slot></span>
      </label>
    `;
    }
};
ViedocRadio.shadowRootOptions = {
    ...LitElement.shadowRootOptions,
    delegatesFocus: true,
};
ViedocRadio.formAssociated = true;
ViedocRadio.styles = [
    css `
      :host {
        display: inline-flex;
      }

      .radio {
        display: flex;
        position: relative;
        align-items: flex-start;
        font-family: var(--font-family-monument);
        font-size: var(--font-size-sm);
        line-height: normal;
      }

      input {
        position: absolute;
        opacity: 0;
        margin: 0;
        font-size: inherit;
        width: 1.5625em;
        height: 1.5625em;
        cursor: pointer;
      }

      input:focus-visible ~ svg {
        border-radius: 4px;
        outline-offset: 2px;
        outline: 5px auto Highlight; /* Firefox */
        outline: 5px auto -webkit-focus-ring-color; /* Chrome and Safari */
      }

      .text {
        color: var(--primary-text-color);
        padding-top: 0.125em;
      }

      svg {
        width: 1.5625em;
        height: 1.5625em;
        margin-right: 0.4375em;
        flex-shrink: 0;
      }

      [dir='rtl'] svg {
        margin-right: 0;
        margin-left: 0.4375em;
      }

      .radio--disabled {
        cursor: default;
      }

      .radio--disabled .text {
        color: var(--color-gray-70);
      }

      .radio--disabled svg {
        opacity: 0.5;
      }
    `,
];
__decorate([
    property({ type: String })
], ViedocRadio.prototype, "value", void 0);
__decorate([
    property({ type: Boolean, reflect: true })
], ViedocRadio.prototype, "checked", void 0);
__decorate([
    property({ type: Boolean, reflect: true })
], ViedocRadio.prototype, "disabled", void 0);
__decorate([
    property({ type: String })
], ViedocRadio.prototype, "name", void 0);
__decorate([
    property({ type: ElementInternals })
], ViedocRadio.prototype, "_internals", void 0);
__decorate([
    query('input')
], ViedocRadio.prototype, "inputElement", void 0);
ViedocRadio = ViedocRadio_1 = __decorate([
    customElement('viedoc-radio')
], ViedocRadio);
export { ViedocRadio };
